import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ClockIcon, MapPinIcon, PlaneIcon, BeachIcon, MountainIcon, CastleIcon, GlobeIcon, StarIcon, WhatsAppIcon } from '../components/Icons';
import api from '../services/api';
import SEO from '../components/SEO';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogPost();
  }, [id]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      const data = await api.getBlogPost(id);
      setPost(data);
      setError(null);
      
      // Fetch related posts (same category)
      if (data.category) {
        const allPosts = await api.getBlogPosts('All');
        const related = allPosts
          .filter(p => p.category === data.category && p.id !== data.id)
          .slice(0, 3);
        setRelatedPosts(related);
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError('Failed to load blog post. Please try again later.');
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  // Icon mapping for dynamic icon selection
  const iconMap = {
    PlaneIcon,
    BeachIcon,
    MountainIcon,
    CastleIcon,
    GlobeIcon,
    StarIcon,
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || GlobeIcon;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
          <p className="mt-4 text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The blog post you\'re looking for doesn\'t exist.'}</p>
          <Link to="/blog" className="text-primary-blue hover:text-primary-orange font-semibold">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = getIcon(post.icon_name || 'GlobeIcon');

  return (
    <div className="flex-1">
      <SEO
        title={post.title}
        description={post.excerpt || (post.content ? post.content.substring(0, 160).replace(/<[^>]*>/g, '') + '...' : `${post.title} - Happy Yatra Travels Travel Blog`)}
        image={post.image_url}
        canonical={`/blog/${id}`}
        type="article"
        publishedTime={post.published_date}
        author={post.author}
        keywords={`${post.title}, ${post.category}, travel blog, Happy Yatra Travels`}
      />
      {/* Hero Image Section */}
      <section className="relative h-96 md:h-[500px] overflow-hidden">
        <img
          src={post.image_url || 'https://images.unsplash.com/photo-1532386236358-a33d8a9434e3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-2 bg-primary-orange text-white text-sm font-semibold rounded-full mb-4">
                {post.category}
              </span>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight">
                {post.title}
              </h1>
              <div className="flex items-center gap-6 text-white/90">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center">
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium">{post.author}</span>
                </div>
                <div className="flex items-center gap-1">
                  <ClockIcon className="w-4 h-4" />
                  <span>{post.read_time || '5 min read'}</span>
                </div>
                <span>•</span>
                <span>{formatDate(post.published_date) || 'Recently'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back Button */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-primary-blue transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </button>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            {post.content ? (
              <div 
                className="blog-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            ) : (
              <div className="text-gray-700 leading-relaxed">
                <p className="mb-6 text-lg">{post.excerpt || 'Content coming soon...'}</p>
              </div>
            )}
          </article>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-blue to-dark-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Experience This Destination?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let us help you plan your perfect trip. Get in touch with our travel experts today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/917718838808?text=Hi, I'm interested in ${post.title}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-primary-orange text-white rounded-xl font-semibold hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-lg inline-flex items-center justify-center gap-2"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Get Travel Consultation
            </a>
            <Link
              to="/packages"
              className="px-8 py-4 bg-white/20 backdrop-blur-md text-white rounded-xl font-semibold hover:bg-white/30 border-2 border-white/50 transform hover:scale-105 transition-all duration-300"
            >
              View Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Posts</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => {
                const RelatedIcon = getIcon(relatedPost.icon_name || 'GlobeIcon');
                return (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.id}`}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={relatedPost.image_url || 'https://images.unsplash.com/photo-1532386236358-a33d8a9434e3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs font-semibold text-primary-orange">{relatedPost.category}</span>
                      <h3 className="text-lg font-bold text-gray-900 mt-2 mb-2 line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2">{relatedPost.excerpt}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetail;
